import { useMemo, useState } from "react";
import { Button, TimePicker, cn } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import type { EditDriverStopTimestampsInput } from "../hooks";
import type { DriverStop } from "../lib/normalize";

/**
 * F14 Slice 3: 打刻修正画面.
 * 当日内 (JST 23:59) のみ修正可能。承認済運転日報配下の stop は API 側で 422 拒否。
 * stop_type に応じて pickup_completed_at (集荷) / completed_at (配送) が
 * route_stops.actual_departure に書き込まれる。
 */
export function TimestampEdit({
  stop,
  isPickup,
  acting,
  error,
  onBack,
  onSubmit,
}: {
  stop: DriverStop;
  /** true: pickup stop (pickup_completed_at を使う) / false: delivery stop (completed_at) */
  isPickup: boolean;
  acting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: (input: EditDriverStopTimestampsInput) => void;
}) {
  // 初期値: stop.actual があればその時刻、なければ stop.time
  const initial = stop.actual ?? stop.time ?? "09:00";
  const [hhmm, setHhmm] = useState<string>(initial.slice(0, 5));
  const [reason, setReason] = useState("");

  const todayJst = useMemo(() => {
    const now = new Date();
    const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return jst.toISOString().slice(0, 10);
  }, []);

  const overLimit = reason.length > 500;
  const reasonEmpty = reason.trim().length === 0;

  function buildPayload(): EditDriverStopTimestampsInput {
    const iso = `${todayJst}T${hhmm}:00+09:00`;
    return {
      ...(isPickup ? { pickup_completed_at: iso } : { completed_at: iso }),
      reason: reason.trim(),
    };
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="打刻修正" sub={stop.name} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">対象の配送</div>
          <div className="mb-0.5 text-[length:var(--fs-md)] font-semibold">{stop.name}</div>
          <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            {isPickup ? "集荷完了時刻" : "配送完了時刻"} の修正
          </div>
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">
            修正後の {isPickup ? "集荷完了時刻" : "配送完了時刻"}
          </div>
          <TimePicker
            value={hhmm}
            onChange={setHhmm}
            disabled={acting}
            aria-label={isPickup ? "集荷完了時刻" : "配送完了時刻"}
            className="w-full justify-center"
          />
          <div className="mt-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            本日 ({todayJst}) のみ修正可能 (承認済日報配下は不可)
          </div>
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">修正理由 (必須)</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="例: 打刻忘れのため後追い入力"
            className="w-full resize-none rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-sunken)] px-3 py-2 text-[length:var(--fs-sm)]"
          />
          <div
            className={cn(
              "mt-1 text-right text-[length:var(--fs-xs)]",
              overLimit ? "text-[var(--danger)]" : "text-[var(--ink-3)]",
            )}
          >
            {reason.length} / 500
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}
      </div>

      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={acting}
          className="h-auto min-h-[var(--row-h)] px-4 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          キャンセル
        </Button>
        <Button
          type="button"
          onClick={() => onSubmit(buildPayload())}
          disabled={acting || overLimit || reasonEmpty}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
            acting && "cursor-wait opacity-70",
          )}
        >
          {acting ? "送信中…" : "打刻を修正する"}
        </Button>
      </BottomBar>
    </div>
  );
}
